/**
 * Wheel of Fortune - Pointer Renderer (Theme System)
 * Phase 7.0+: Supports "classic" and "gem" pointer styles
 */

import * as PIXI from 'pixi.js';
import { WHEEL_THEMES, type WheelTheme } from './themes/wheelThemes';

function parseColor(color: string): number {
  return parseInt(color.replace('#', ''), 16);
}

export class PointerRenderer extends PIXI.Container {
  private theme: WheelTheme;
  private radius: number;

  constructor(themeId: string = 'modern', radius: number) {
    super();

    const key = (themeId as keyof typeof WHEEL_THEMES) || 'modern';
    this.theme = WHEEL_THEMES[key] || WHEEL_THEMES.modern;
    this.radius = radius;

    this.render();
  }

  private render(): void {
    // Clear previous
    this.removeChildren();

    const { pointer } = this.theme;

    if (pointer.style === 'gem') {
      this.renderGemPointer();
    } else {
      this.renderClassicPointer();
    }
  }

  /**
   * Render classic triangle pointer
   */
  private renderClassicPointer(): void {
    const { pointer } = this.theme;
    const size = 35;
    const yOffset = -this.radius - 15;

    // Parse gradient colors
    const [_color1, color2, _color3] = pointer.fillGradient;
    const mainColor = parseColor(color2);

    // Layer 1: Shadow (behind pointer)
    const shadow = new PIXI.Graphics();
    shadow.beginFill(parseColor(pointer.shadow.color), pointer.shadow.alpha);
    shadow.moveTo(0, yOffset + pointer.shadow.y);
    shadow.lineTo(-size / 2 + 2, yOffset - size + pointer.shadow.y);
    shadow.lineTo(size / 2 + 2, yOffset - size + pointer.shadow.y);
    shadow.closePath();
    shadow.endFill();
    shadow.filters = [new PIXI.BlurFilter(pointer.shadow.blur)];
    this.addChild(shadow);

    // Layer 2: Main pointer
    const pointerGraphics = new PIXI.Graphics();
    pointerGraphics.beginFill(mainColor);
    pointerGraphics.lineStyle(pointer.stroke.width, parseColor(pointer.stroke.color), pointer.stroke.alpha);
    pointerGraphics.moveTo(0, yOffset);
    pointerGraphics.lineTo(-size / 2, yOffset - size);
    pointerGraphics.lineTo(size / 2, yOffset - size);
    pointerGraphics.closePath();
    pointerGraphics.endFill();
    this.addChild(pointerGraphics);

    // Layer 3: Highlight (inner light)
    const highlight = new PIXI.Graphics();
    highlight.beginFill(0xffffff, 0.4);
    highlight.moveTo(0, yOffset - 5);
    highlight.lineTo(-size / 4, yOffset - size / 2);
    highlight.lineTo(size / 4, yOffset - size / 2);
    highlight.closePath();
    highlight.endFill();
    this.addChild(highlight);

    // Layer 4: Glow around pointer
    const glow = new PIXI.Graphics();
    glow.beginFill(mainColor, 0.3);
    glow.moveTo(0, yOffset);
    glow.lineTo(-size / 2 - 8, yOffset - size - 8);
    glow.lineTo(size / 2 + 8, yOffset - size - 8);
    glow.closePath();
    glow.endFill();
    glow.filters = [new PIXI.BlurFilter(10)];
    this.addChild(glow);
  }

  /**
   * Render gem/jewel pointer (more luxe)
   */
  private renderGemPointer(): void {
    const { pointer } = this.theme;
    const size = 40;
    const yOffset = -this.radius - 18;

    // Parse gradient colors
    const [color1, color2, color3] = pointer.fillGradient;
    const topColor = parseColor(color1);
    const midColor = parseColor(color2);
    const bottomColor = parseColor(color3);

    // Layer 1: Shadow
    const shadow = new PIXI.Graphics();
    shadow.beginFill(parseColor(pointer.shadow.color), pointer.shadow.alpha);
    shadow.moveTo(0, yOffset + pointer.shadow.y);
    shadow.lineTo(-size / 2 + 2, yOffset - size * 0.7 + pointer.shadow.y);
    shadow.lineTo(-size / 3 + 2, yOffset - size + pointer.shadow.y);
    shadow.lineTo(size / 3 + 2, yOffset - size + pointer.shadow.y);
    shadow.lineTo(size / 2 + 2, yOffset - size * 0.7 + pointer.shadow.y);
    shadow.closePath();
    shadow.endFill();
    shadow.filters = [new PIXI.BlurFilter(pointer.shadow.blur)];
    this.addChild(shadow);

    // Layer 2: Bottom facet (darker)
    const bottomFacet = new PIXI.Graphics();
    bottomFacet.beginFill(bottomColor);
    bottomFacet.moveTo(0, yOffset);
    bottomFacet.lineTo(-size / 2, yOffset - size * 0.6);
    bottomFacet.lineTo(0, yOffset - size * 0.7);
    bottomFacet.lineTo(size / 2, yOffset - size * 0.6);
    bottomFacet.closePath();
    bottomFacet.endFill();
    this.addChild(bottomFacet);

    // Layer 3: Top facet (lighter, gem cut)
    const topFacet = new PIXI.Graphics();
    topFacet.beginFill(midColor);
    topFacet.lineStyle(pointer.stroke.width, parseColor(pointer.stroke.color), pointer.stroke.alpha);
    topFacet.moveTo(0, yOffset - size * 0.7);
    topFacet.lineTo(-size / 3, yOffset - size);
    topFacet.lineTo(size / 3, yOffset - size);
    topFacet.closePath();
    topFacet.endFill();
    this.addChild(topFacet);

    // Layer 4: Highlight sparkle (top-left)
    const sparkle = new PIXI.Graphics();
    sparkle.beginFill(0xffffff, 0.65);
    sparkle.moveTo(-size / 6, yOffset - size * 0.85);
    sparkle.lineTo(-size / 8, yOffset - size * 0.75);
    sparkle.lineTo(0, yOffset - size * 0.8);
    sparkle.closePath();
    sparkle.endFill();
    this.addChild(sparkle);

    // Layer 5: Outer glow
    const glow = new PIXI.Graphics();
    glow.beginFill(topColor, 0.35);
    glow.moveTo(0, yOffset);
    glow.lineTo(-size / 2 - 10, yOffset - size * 0.6 - 6);
    glow.lineTo(-size / 3 - 6, yOffset - size - 8);
    glow.lineTo(size / 3 + 6, yOffset - size - 8);
    glow.lineTo(size / 2 + 10, yOffset - size * 0.6 - 6);
    glow.closePath();
    glow.endFill();
    glow.filters = [new PIXI.BlurFilter(12)];
    this.addChild(glow);
  }

  public updateTheme(themeId: string): void {
    const key = (themeId as keyof typeof WHEEL_THEMES) || 'modern';
    this.theme = WHEEL_THEMES[key] || WHEEL_THEMES.modern;
    this.removeChildren();
    this.render();
  }

  /**
   * Tick animation (micro bounce when crossing segment)
   */
  public tick(): void {
    // Quick scale bounce
    this.scale.set(1.15);
    setTimeout(() => {
      this.scale.set(1);
    }, 100);
  }
}
